import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import {type PropsWithChildren} from 'react'
import {type AtpAgent, type AtpSessionEvent} from '@atproto/api'

import {msUntilTokenNeedsRefresh} from '#/lib/jwt'
import {logger} from '#/logger'
import {isWeb} from '#/platform/detection'
import * as persisted from '#/state/persisted'
import {useCloseAllActiveElements} from '#/state/util'
import {useGlobalDialogsControlContext} from '#/components/dialogs/Context'
import {AnalyticsContext, useAnalyticsBase, utils} from '#/analytics'
import {IS_WEB} from '#/env'
import {emitSessionDropped} from '../events'
import {
  agentToSessionAccount,
  createAgentAndCreateAccount,
  createAgentAndLogin,
  createAgentAndResume,
  sessionAccountToSession,
  type WsocAgent,
} from './agent'
import {syncSessionCookies} from './cookies'
import {type Action, getInitialState, reducer, type State} from './reducer'
import {
  fetchSilentReauthWithRetry,
  PermanentReauthFailureError,
} from './silentReauth'

export {isSignupQueued} from './util'
import {addSessionDebugLog} from './logging'
export type {SessionAccount} from '#/state/session/types'

import {clearPersistedQueryStorage} from '#/lib/persisted-query-storage'
import {
  type SessionApiContext,
  type SessionStateContext,
} from '#/state/session/types'
import {
  getWsocialPublicAgent,
  shouldRouteToPublicAppview,
} from '#/state/session/wsocialPublicAgent.ts'
import {useOnboardingDispatch} from '#/state/shell/onboarding'
import {
  clearAgeAssuranceServerDataForAll,
  clearAgeAssuranceServerDataForDid,
} from '#/ageAssurance/data'
import {type WsocialProfile} from '#/wsocial/types/profile'

// const WSOCIAL_VERIFIED_DOMAINS = ['wsocial.eu', 'wsocial.dev'] as const

const StateContext = createContext<SessionStateContext>({
  accounts: [],
  currentAccount: undefined,
  hasSession: false,
  isInitialLoad: true,
})
StateContext.displayName = 'SessionStateContext'

const AgentContext = createContext<WsocAgent | null>(null)
AgentContext.displayName = 'SessionAgentContext'

const ApiContext = createContext<SessionApiContext>({
  createAccount: async () => {},
  login: async () => {},
  logoutCurrentAccount: () => {},
  logoutEveryAccount: () => {},
  resumeSession: () => Promise.resolve(false),
  removeAccount: () => {},
  partialRefreshSession: async () => {},
  markSessionReady: () => {},
})
ApiContext.displayName = 'SessionApiContext'

class SessionStore {
  private state: State
  private listeners = new Set<() => void>()

  constructor() {
    // Careful: By the time this runs, `persisted` needs to already be filled.
    const initialState = getInitialState(persisted.get('session').accounts)
    addSessionDebugLog({type: 'reducer:init', state: initialState})
    this.state = initialState
  }

  getState = (): State => {
    return this.state
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  dispatch = (action: Action) => {
    const nextState = reducer(this.state, action)
    this.state = nextState
    // Persist synchronously without waiting for the React render cycle.
    if (nextState.needsPersist) {
      nextState.needsPersist = false
      const currentAccount = nextState.accounts.find(
        a => a.did === nextState.currentAgentState.did,
      )
      const persistedData = {
        accounts: nextState.accounts,
        currentAccount,
      }
      addSessionDebugLog({type: 'persisted:broadcast', data: persistedData})
      void persisted.write('session', persistedData)
      syncSessionCookies(currentAccount)
    }
    this.listeners.forEach(listener => listener())
  }
}

export function Provider({children}: PropsWithChildren<{}>) {
  const ax = useAnalyticsBase()
  const cancelPendingTask = useOneTaskAtATime()
  // Background refreshes get their own abort slot — never share with
  // login/resumeSession or a partialRefreshSession firing mid-switch would
  // abort the switch's dispatch and leave the app on the previous account.
  const cancelPendingRefresh = useOneTaskAtATime()
  const isSwitchingRef = useRef(false)
  // eslint-disable-next-line react/hook-use-state
  const [store] = useState(() => new SessionStore())
  const state = useSyncExternalStore(store.subscribe, store.getState)
  const onboardingDispatch = useOnboardingDispatch()
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Deduplicates concurrent silent reauth attempts for the same DID.
  // Prevents a second expired-event from consuming the same (potentially
  // single-use) reauthJwt while the first attempt is already in flight.
  const pendingReauths = useRef(new Map<string, Promise<void>>())

  const onAgentSessionChange = useCallback(
    (agent: AtpAgent, accountDid: string, sessionEvent: AtpSessionEvent) =>
      void (async () => {
        const refreshedAccount = agentToSessionAccount(agent) // Mutable, so snapshot it right away.

        if (sessionEvent === 'expired' || sessionEvent === 'create-failed') {
          const stored = persisted
            .get('session')
            .accounts.find(a => a.did === accountDid)

          if (stored?.reauthJwt) {
            // Capture before the async closure — TypeScript can't narrow object
            // properties across async boundaries.
            const reauthJwt = stored.reauthJwt

            // If a reauth is already in-flight for this DID, skip — the first
            // invocation handles it. This prevents concurrent expired-events from
            // each consuming the same (potentially single-use) reauthJwt.
            if (pendingReauths.current.has(accountDid)) {
              return
            }

            let outcome: 'success' | 'permanent' | 'exhausted' = 'success'

            const reauthPromise: Promise<void> = (async () => {
              try {
                const result = await fetchSilentReauthWithRetry(
                  agent,
                  reauthJwt,
                )
                // Inject fresh tokens into the agent without triggering a new event.
                agent.sessionManager.session = {
                  ...sessionAccountToSession(stored),
                  accessJwt: result.accessJwt,
                  refreshJwt: result.refreshJwt,
                }
                store.dispatch({
                  type: 'silent-reauth-success',
                  accountDid,
                  accessJwt: result.accessJwt,
                  refreshJwt: result.refreshJwt,
                })
              } catch (err) {
                if (err instanceof PermanentReauthFailureError) {
                  outcome = 'permanent'
                } else {
                  // ExhaustedReauthAttemptsError or unexpected error
                  outcome = 'exhausted'
                }
              }
            })()

            pendingReauths.current.set(accountDid, reauthPromise)
            try {
              await reauthPromise
            } finally {
              pendingReauths.current.delete(accountDid)
            }

            if (outcome === 'success') return

            if (outcome === 'exhausted') {
              // All retry budget spent — log out and hard-navigate to root so
              // SSR/middleware can issue the correct redirect to the sign-in page.
              store.dispatch({type: 'logged-out-current-account'})
              emitSessionDropped()
              if (isWeb && typeof window !== 'undefined') {
                window.location.replace('/')
              }
              return
            }

            // permanent failure — clear reauthJwt so we don't retry it again
            emitSessionDropped()
            store.dispatch({
              type: 'received-agent-event',
              agent,
              refreshedAccount,
              accountDid,
              sessionEvent,
              clearReauthJwt: true,
            })
            return
          }

          emitSessionDropped()
        }

        store.dispatch({
          type: 'received-agent-event',
          agent,
          refreshedAccount,
          accountDid,
          sessionEvent,
        })
      })(),
    [store],
  )

  const createAccount = useCallback<SessionApiContext['createAccount']>(
    async (params, metrics) => {
      addSessionDebugLog({type: 'method:start', method: 'createAccount'})
      const signal = cancelPendingTask()
      ax.metric('account:create:begin', {})
      const {agent, account} = await createAgentAndCreateAccount(
        params,
        onAgentSessionChange,
      )

      if (signal.aborted) {
        return
      }
      store.dispatch({
        type: 'switched-to-account',
        newAgent: agent,
        newAccount: account,
      })
      ax.metric('account:create:success', metrics, {
        session: utils.accountToSessionMetadata(account),
      })
      addSessionDebugLog({type: 'method:end', method: 'createAccount', account})
    },
    [ax, store, onAgentSessionChange, cancelPendingTask],
  )

  const login = useCallback<SessionApiContext['login']>(
    async (params, logContext) => {
      addSessionDebugLog({type: 'method:start', method: 'login'})
      const signal = cancelPendingTask()
      const {agent, account} = await createAgentAndLogin(
        params,
        onAgentSessionChange,
      )

      if (signal.aborted) {
        return
      }
      store.dispatch({
        type: 'switched-to-account',
        newAgent: agent,
        newAccount: account,
      })
      ax.metric(
        'account:loggedIn',
        {logContext, withPassword: true},
        {session: utils.accountToSessionMetadata(account)},
      )
      addSessionDebugLog({type: 'method:end', method: 'login', account})
    },
    [ax, store, onAgentSessionChange, cancelPendingTask],
  )

  const logoutCurrentAccount = useCallback<
    SessionApiContext['logoutCurrentAccount']
  >(
    logContext => {
      addSessionDebugLog({type: 'method:start', method: 'logout'})
      cancelPendingTask()
      void persisted.write('session', {accounts: [], currentAccount: undefined})
      const prevState = store.getState()
      store.dispatch({
        type: 'logged-out-current-account',
      })
      ax.metric(
        'account:loggedOut',
        {logContext, scope: 'current'},
        {
          session: utils.accountToSessionMetadata(
            prevState.accounts.find(
              a => a.did === prevState.currentAgentState.did,
            ),
          ),
        },
      )
      addSessionDebugLog({type: 'method:end', method: 'logout'})
      if (prevState.currentAgentState.did) {
        clearAgeAssuranceServerDataForDid({
          did: prevState.currentAgentState.did,
        })
        void clearPersistedQueryStorage(prevState.currentAgentState.did)
      }
      // reset onboarding flow on logout
      onboardingDispatch({type: 'skip'})
    },
    [ax, store, cancelPendingTask, onboardingDispatch],
  )

  const logoutEveryAccount = useCallback<
    SessionApiContext['logoutEveryAccount']
  >(
    logContext => {
      addSessionDebugLog({type: 'method:start', method: 'logout'})
      cancelPendingTask()
      void persisted.write('session', {accounts: [], currentAccount: undefined})
      const prevState = store.getState()
      store.dispatch({
        type: 'logged-out-every-account',
      })
      ax.metric(
        'account:loggedOut',
        {logContext, scope: 'every'},
        {
          session: utils.accountToSessionMetadata(
            prevState.accounts.find(
              a => a.did === prevState.currentAgentState.did,
            ),
          ),
        },
      )
      addSessionDebugLog({type: 'method:end', method: 'logout'})
      clearAgeAssuranceServerDataForAll()
      for (const account of prevState.accounts) {
        void clearPersistedQueryStorage(account.did)
      }
      // reset onboarding flow on logout
      onboardingDispatch({type: 'skip'})
    },
    [store, cancelPendingTask, onboardingDispatch, ax],
  )

  const resumeSession = useCallback<SessionApiContext['resumeSession']>(
    async (storedAccount, isSwitchingAccounts = false) => {
      addSessionDebugLog({
        type: 'method:start',
        method: 'resumeSession',
        account: storedAccount,
      })
      if (isSwitchingAccounts) {
        isSwitchingRef.current = true
      }
      const signal = cancelPendingTask()
      const {agent, account} = await createAgentAndResume(
        storedAccount,
        onAgentSessionChange,
      )

      if (isSwitchingAccounts) {
        isSwitchingRef.current = false
      }
      if (signal.aborted) {
        return false
      }
      store.dispatch({
        type: 'switched-to-account',
        newAgent: agent,
        newAccount: account,
      })
      addSessionDebugLog({type: 'method:end', method: 'resumeSession', account})
      if (isSwitchingAccounts) {
        // reset onboarding flow on switch account
        onboardingDispatch({type: 'skip'})
      }
      // Stored handle/email can be stale: when the access token is still valid,
      // createAgentAndResume reuses the persisted session and skips the network
      // getSession, so identity changes (handle re-verification, email update)
      // never refresh until logout/login. Pull a fresh snapshot in the
      // background so they self-heal on load. Best-effort; uses the resumed
      // agent directly to avoid a stale closure over `state`.
      agent.com.atproto.server
        .getSession()
        .then(({data}) => {
          if (signal.aborted || !agent.session) return
          store.dispatch({
            type: 'partial-refresh-session',
            accountDid: agent.session.did,
            patch: {
              handle: data.handle,
              email: data.email,
              emailConfirmed: data.emailConfirmed,
              emailAuthFactor: data.emailAuthFactor,
              wsocialVerified: (data as WsocialProfile).wsocialVerified,
            },
          })
        })
        .catch(() => {})
      return true
    },
    [store, onAgentSessionChange, cancelPendingTask, onboardingDispatch],
  )

  const partialRefreshSession = useCallback<
    SessionApiContext['partialRefreshSession']
  >(async () => {
    const signal = cancelPendingRefresh()
    const agent = state.currentAgentState.agent as WsocAgent
    const {data} = await agent.com.atproto.server.getSession()
    if (signal.aborted) return
    store.dispatch({
      type: 'partial-refresh-session',
      accountDid: agent.session!.did,
      patch: {
        handle: data.handle,
        email: data.email,
        emailConfirmed: data.emailConfirmed,
        emailAuthFactor: data.emailAuthFactor,
        wsocialVerified: (data as WsocialProfile).wsocialVerified,
      },
    })
  }, [store, state, cancelPendingRefresh])

  const removeAccount = useCallback<SessionApiContext['removeAccount']>(
    account => {
      addSessionDebugLog({
        type: 'method:start',
        method: 'removeAccount',
        account,
      })
      cancelPendingTask()
      store.dispatch({
        type: 'removed-account',
        accountDid: account.did,
      })
      addSessionDebugLog({type: 'method:end', method: 'removeAccount', account})
      clearAgeAssuranceServerDataForDid({did: account.did})
    },
    [store, cancelPendingTask],
  )
  useEffect(() => {
    return persisted.onUpdate('session', nextSession => {
      const synced = nextSession
      addSessionDebugLog({type: 'persisted:receive', data: synced})
      store.dispatch({
        type: 'synced-accounts',
        syncedAccounts: synced.accounts,
        syncedCurrentDid: synced.currentAccount?.did,
      })
      const syncedAccount = synced.accounts.find(
        a => a.did === synced.currentAccount?.did,
      )
      syncSessionCookies(syncedAccount)
      if (syncedAccount && syncedAccount.refreshJwt) {
        // Read live state from the store rather than the stale closure value.
        const currentState = store.getState()
        if (
          syncedAccount.did !== currentState.currentAgentState.did &&
          !isSwitchingRef.current
        ) {
          void resumeSession(syncedAccount)
        } else {
          const agent = currentState.currentAgentState.agent as AtpAgent
          const prevSession = agent.session
          // eslint-disable-next-line react-compiler/react-compiler
          agent.sessionManager.session = sessionAccountToSession(syncedAccount)
          addSessionDebugLog({
            type: 'agent:patch',
            agent,
            prevSession,
            nextSession: agent.session,
          })
        }
      }
    })
  }, [store, resumeSession])

  const markSessionReady = useCallback(() => {
    setIsInitialLoad(false)
  }, [])

  const stateContext = useMemo(() => {
    const accounts = state.accounts
    const currentAccount = state.accounts.find(
      a => a.did === state.currentAgentState.did,
    )

    return {
      accounts,
      currentAccount,
      hasSession: !!state.currentAgentState.did,
      isInitialLoad,
    }
  }, [state, isInitialLoad])

  const api = useMemo(
    () => ({
      createAccount,
      login,
      logoutCurrentAccount,
      logoutEveryAccount,
      resumeSession,
      removeAccount,
      partialRefreshSession,
      markSessionReady,
    }),
    [
      createAccount,
      login,
      logoutCurrentAccount,
      logoutEveryAccount,
      resumeSession,
      removeAccount,
      partialRefreshSession,
      markSessionReady,
    ],
  )

  // @ts-expect-error window type is not declared, debug only
  // eslint-disable-next-line react-hooks/immutability
  if (__DEV__ && IS_WEB) window.agent = state.currentAgentState.agent

  const agent = state.currentAgentState.agent as WsocAgent
  const currentAgentRef = useRef(agent)
  useEffect(() => {
    if (currentAgentRef.current !== agent) {
      // Read the previous value and immediately advance the pointer.
      const prevAgent = currentAgentRef.current
      currentAgentRef.current = agent
      addSessionDebugLog({type: 'agent:switch', prevAgent, nextAgent: agent})
      // We never reuse agents so let's fully neutralize the previous one.
      // This ensures it won't try to consume any refresh tokens.
      prevAgent.dispose()
    }
  }, [agent])

  // Always-fresh agent ref for use inside the proactive-refresh timer callback.
  const proactiveRefreshAgentRef = useRef(agent)
  proactiveRefreshAgentRef.current = agent

  const currentDid = state.currentAgentState.did
  // Recompute only when the token itself changes (after each refresh or login).
  const currentAccessJwt = useMemo(
    () => state.accounts.find(a => a.did === currentDid)?.accessJwt ?? null,
    [state.accounts, currentDid],
  )

  useEffect(() => {
    if (!currentDid || !currentAccessJwt) return

    const delay = msUntilTokenNeedsRefresh(currentAccessJwt)
    if (delay === null) return // JWT missing iat/exp — cannot schedule

    const doRefresh = () => {
      proactiveRefreshAgentRef.current.sessionManager
        .refreshSession()
        .catch((e: unknown) => {
          logger.error('session: proactive token refresh failed', {message: e})
        })
    }

    if (delay === 0) {
      // Already past the 2/3 threshold — refresh immediately
      doRefresh()
      return
    }

    const timer = setTimeout(doRefresh, delay)
    return () => clearTimeout(timer)
    // Re-run whenever the token changes (new accessJwt after a successful refresh
    // triggers this effect, which schedules the next refresh timer automatically).
  }, [currentDid, currentAccessJwt])

  return (
    <AgentContext.Provider value={agent}>
      <StateContext.Provider value={stateContext}>
        <ApiContext.Provider value={api}>
          <AnalyticsContext
            metadata={utils.useMeta({
              session: utils.accountToSessionMetadata(
                stateContext.currentAccount,
              ),
            })}>
            {children}
          </AnalyticsContext>
        </ApiContext.Provider>
      </StateContext.Provider>
    </AgentContext.Provider>
  )
}

function useOneTaskAtATime() {
  const abortController = useRef<AbortController | null>(null)
  const cancelPendingTask = useCallback(() => {
    if (abortController.current) {
      abortController.current.abort()
    }
    abortController.current = new AbortController()
    return abortController.current.signal
  }, [])
  return cancelPendingTask
}

export function useSession() {
  return useContext(StateContext)
}

export function useWsocialVerified(): string | null | boolean {
  const session = useSession()
  return useMemo(() => {
    return session.currentAccount?.wsocialVerified ?? null

    /*const handle = session.currentAccount?.handle
    if (
      handle &&
      WSOCIAL_VERIFIED_DOMAINS.some(domain => handle.endsWith(`.${domain}`))
    ) {
      return session.currentAccount?.wsocialVerified ?? null
    }
    return null*/
  }, [session])
}

export function useSessionApi() {
  return useContext(ApiContext)
}

export function useRequireAuth() {
  const {hasSession} = useSession()
  const closeAll = useCloseAllActiveElements()
  const {signinDialogControl} = useGlobalDialogsControlContext()

  return useCallback(
    (fn: () => unknown) => {
      if (hasSession) {
        fn()
      } else {
        closeAll()
        signinDialogControl.open()
      }
    },
    [hasSession, signinDialogControl, closeAll],
  )
}

export function useAgent(nsids?: string[]): WsocAgent {
  const {hasSession} = useSession()

  const agent = useContext(AgentContext)
  if (!agent) {
    throw Error('useAgent() must be below <SessionProvider>.')
  }
  return useMemo(() => {
    if (!hasSession && nsids?.length && shouldRouteToPublicAppview(nsids)) {
      return getWsocialPublicAgent()
    }
    return agent
  }, [hasSession, agent, nsids])
}

export function useWsocAgent(nsids?: string[]): WsocAgent {
  return useAgent(nsids)
}

export type {BskyAppAgent} from './bskyAppAgent'
export type {WsocAgent} from './wsocAgent'
